/**
 * Why this board's cost is not the company's.
 *
 * This page used to say the totals matched. They do not: revenue ties exactly,
 * cost is $867,662.53 short -- 7.06 margin points -- and someone carrying
 * "53.6% margin" from here into a conversation where the company number is
 * 46.5% is the failure this note exists to prevent. The old sentence actively
 * encouraged it.
 *
 * Deliberately NOT styled as an error. Nothing here is broken: the board is
 * delivery demands and the missing cost is real cost that no demand-level view
 * can reach. It is a boundary, and it reads as one -- the same quiet grey line
 * CostBasisNote uses, with the arithmetic behind a disclosure for anyone who
 * wants to check it rather than take it on faith.
 *
 * The exception is `unexplained_cost`. Zero means the two named causes account
 * for the entire gap. Anything else means a third cause exists that nobody has
 * named, which IS a problem, and it gets said plainly instead of being averaged
 * into a reassuring sentence.
 */
import { ChevronDown, ChevronRight } from "lucide-react";
import { useId, useState } from "react";
import { money } from "@/lib/format";
import type { TechnologyReconciliation } from "@/lib/profitability-api";

export function ReconciliationNote({
  reconciliation,
}: {
  reconciliation: TechnologyReconciliation | undefined;
}) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  if (!reconciliation) return null;

  const {
    company_cost,
    board_cost,
    cost_difference,
    account_management_cost,
    cost_not_attributed_to_a_demand,
    unexplained_cost,
    explained,
    company_margin_pct,
    board_margin_pct,
    margin_point_difference,
  } = reconciliation;

  return (
    <div className="text-xs text-muted-foreground">
      <p>
        Revenue ties to the company total. Cost does not: this board is{" "}
        <span className="font-medium text-foreground">{money(cost_difference)}</span> below it,
        worth {margin_point_difference === null ? "an unknown number of" : margin_point_difference}{" "}
        margin points. Do not compare this margin with the company's.
      </p>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
        className="mt-1 inline-flex items-center gap-1 transition-colors hover:text-foreground"
      >
        {open ? (
          <ChevronDown className="size-3" aria-hidden />
        ) : (
          <ChevronRight className="size-3" aria-hidden />
        )}
        Where the difference goes
      </button>

      {open ? (
        <div id={panelId} className="mt-2 space-y-3 rounded-md border bg-muted/30 p-3">
          <dl className="grid gap-x-6 gap-y-1 sm:grid-cols-[16rem_1fr]">
            <dt className="font-medium text-foreground">Company cost</dt>
            <dd>
              {money(company_cost)}
              {company_margin_pct === null ? null : ` · ${company_margin_pct}% margin`}
            </dd>
            <dt className="font-medium text-foreground">This board</dt>
            <dd>
              {money(board_cost)}
              {board_margin_pct === null ? null : ` · ${board_margin_pct}% margin`}
            </dd>
            <dt className="font-medium text-foreground">Account Management</dt>
            <dd>{money(account_management_cost)} — cost against no revenue</dd>
            <dt className="font-medium text-foreground">No demand attached</dt>
            <dd>{money(cost_not_attributed_to_a_demand)} — booked at account level</dd>
          </dl>

          {/* The only line here that can be bad news, so it is the only one
              allowed to look like it. */}
          {explained && unexplained_cost === 0 ? (
            <p>Those two account for the difference exactly. Nothing is unexplained.</p>
          ) : (
            <p className="font-medium text-destructive">
              {money(unexplained_cost)} of the difference is not explained by either cause. A third
              one exists and has not been named — treat every figure on this page as provisional
              until it is.
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
}
